import { Box, Button, Stack, Typography, styled } from '@mui/material';
import LoadingScreen from 'src/minimals/LoadingScreen';
import Page from 'src/minimals/Page';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { removeEmailedInvitation } from 'src/redux/slices/invitations';
import {
  acceptCustomerInvitation,
  rejectCustomerInvitation,
} from 'src/redux/thunks/customers';
import { getCurrentUser } from 'src/redux/thunks/user';
import { workspaceSelectors, userSelectors } from 'src/selectors';

const CustomerInvitation = ({ action }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const navigate = useNavigate();
  const user = useSelector(userSelectors.getUser());
  const token = new URL(window.location.href).searchParams.get('token');
  const invitationInfo = useSelector(workspaceSelectors.getInvitationInfo());
  const dispatch = useDispatch();

  // Only meaningful on the accept path, which runs behind WorkspaceInvitationGuard
  // and therefore always has a logged-in user and a decoded invitation. Rejecting
  // needs neither: the backend resolves the invitation from the token alone (the
  // rejectInvitation mutation is unauthenticated), so `user` is null and
  // `invitationInfo` is empty for a logged-out recipient.
  const isUserEmailNotValid =
    action === 'accept' &&
    Boolean(user?.email) &&
    Boolean(invitationInfo?.email) &&
    user.email.trim().toLowerCase() !==
      invitationInfo.email.trim().toLowerCase();

  useEffect(() => {
    if (action !== 'accept' && action !== 'reject') {
      setLoading(false);
      return;
    }

    if (!token) {
      setError(new Error('MISSING_TOKEN'));
      setLoading(false);
      return;
    }

    if (action === 'accept') {
      dispatch(acceptCustomerInvitation({ token }))
        .unwrap()
        .then(() => {
          dispatch(getCurrentUser());
          // TODO: posiblemente mejorar
          // if (!hasWorkspaces) dispatch(setCustomerMode());
        })
        .catch((error) => setError(error))
        .finally(() => setLoading(false));
    }

    if (action === 'reject') {
      dispatch(rejectCustomerInvitation({ token }))
        .unwrap()
        .catch((error) => setError(error))
        .finally(() => setLoading(false));
    }
  }, [dispatch, action, token]);

  if (loading) return <LoadingScreen />;

  return (
    <RootStyle title={t('page.workspace.invitation')}>
      <ContentStyle>
        <Stack direction="row" alignItems="center">
          <Box sx={{ flexGrow: 1 }}>
            <Box
              component="img"
              alt="empty content"
              src="/static/illustrations/illustration_components.png"
              sx={{ height: 288, margin: 'auto', mb: 3 }}
            />
            <Stack alignItems="center">
              <Typography
                variant="body2"
                align="center"
                sx={{ color: 'text.secondary', mb: 3 }}
              >
                {!error
                  ? t('workspaceInvitation.body.accept_reject_success')
                  : isUserEmailNotValid
                    ? t('workspaceInvitation.body.error.emailMismatch')
                    : t('workspaceInvitation.body.error.text')}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  dispatch(removeEmailedInvitation());
                  navigate('/');
                }}
              >
                {t('customer_invitation.button.go_home')}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </ContentStyle>
    </RootStyle>
  );
};

const RootStyle = styled(Page)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 650,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
  flexGrow: 1,
}));

export default CustomerInvitation;
